import { useCallback } from 'react'

import { pruneNilValues } from '@core/object'
import {
  appContextToTrackingDataPixel,
  sendPixelTrackingWithSession,
} from '@core/tracking/senders'
import { useStore } from '@core/widgets/store'
import { paramsSelector } from '@core/widgets/store/selectors'
import { FEED_CREATE_EMBED_INSTANCE } from '@core/widgets/tracking/endpoints'
import { getWidgetPositionData } from '@core/widgets/tracking/getWidgetPositionData'
import type { AnyWidgetAppContext } from '@embed/hooks/useEmbedAppContext'

/**
 * Sends Embed Instance pixel event.
 */
export const useTrackEmbedInstance = () => {
  const params = useStore(paramsSelector)

  return useCallback(
    (appContext: AnyWidgetAppContext) => {
      const pixelTrackingData = appContextToTrackingDataPixel({ appContext })

      const positionData = getWidgetPositionData(params.target)

      const pixelData = {
        event_properties: pruneNilValues({
          autoplay: params.autoplay,
          placement: params.placement,
          widget_size: params.size,
          auto_embed_widget: params.auto_embed_widget,
          ui_player_style: params?.ui_player_style,
          ui_theme: params?.theme || params?.ui_theme,
          ...addCohortParams(params),
          ...pixelTrackingData,
          ...positionData,
        }),
      }

      sendPixelTrackingWithSession({
        endpoint: FEED_CREATE_EMBED_INSTANCE,
        data: pixelData,
        host: params.api_host_tracking,
      })
    },
    [params],
  )
}

/**
 * Adds cohort parameters to the pixel data.
 */
const addCohortParams = (params: any) => {
  const cohortParams = {
    cohort_url: params?.cohort_url,
    cohort_category: params?.cohort_category,
    cohort_country_code: params?.cohort_country_code,
    cohort_segment: params?.cohort_segment,
  }

  return pruneNilValues(cohortParams)
}
